import React, { useEffect, useState } from 'react';
import { Button, Carousel} from 'antd';
import { SyncOutlined } from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import axios from 'axios';
import { bindActionCreators } from 'redux';
import { useDispatch, useSelector } from 'react-redux';
import Header from './header';
import { actionCreators } from '../state';
import { formatErrorMessage } from '../utils/errorHandler';
import { logError } from '../utils/logger';

import handIcon from './../assets/img/hand-icon.svg';
import locationIcon from './../assets/img/location-pin-icon-blue.svg';
import dateIcon from './../assets/img/date-icon.svg';
import appointmentBooked from './../assets/img/appointment-booked-icon.svg';
import infoSubmitted from './../assets/img/info-submitted.svg';
import phoneIcon from './../assets/img/phone-icon-submissions-page.svg';
import homeIcon from './../assets/img/home-icon.svg';
import ErrorModal from './organisms/ErrorModal';

const API_URL = process.env.REACT_APP_DEVELOPMENT_API_URL;

const AppointmentDetails = ({ label, value, icon }) => (
  <div className="appointment-info">
    <div className="appointment-icon">
      <img src={icon} alt={label} />
    </div>
    <div className="appointment-text-info">
      <span className="appointment-title">{label}</span>
      <span className="appointment-value">{value}</span>
    </div>
  </div>
);

const SubmitButton = ({ disabled, onClick, text, language = null, style = {} }) => (
  <Button className="additional-info-btn" loading={disabled} size="large" onClick={() => onClick(language)} style={style} disabled={disabled}>
    {text}
  </Button>
);

const AppointmentSubmitted = () => {
  const [apiErrorMessage, setApiErrorMessage] = useState(false);
  const [loading, setLoading] = useState(false);
  const [guid, setGuid] = useState('');
  const dispatch = useDispatch();
  const actions = bindActionCreators(actionCreators, dispatch);
  const location = useLocation();
  const navigate = useNavigate();

  const insuranceTypes = useSelector((state) => state.insuranceTypes);
  const clinicsSearchFields = useSelector((state) => state.clinics);
  const sessionId = useSelector((store) => store.auth.sessionId);

  const insuranceName =
    clinicsSearchFields['insuranceProviderId'] !== 0 &&
    insuranceTypes.length
      ? insuranceTypes.find((item) => clinicsSearchFields['insuranceProviderId'] === item.id)?.name
      : null;

  useEffect(() => {
    if (location.state !== null) {
      completeBookAppointment();
    }
  }, []);

  const completeBookAppointment = async () => {
    const bookAppointmentPayload = { ...location.state.bookAppointmentPayload, PayerNameSelected: insuranceName };
    
    try {
      setLoading(true)
      const controller = new AbortController();
      const id = setTimeout(() => controller.abort(), process.env.REACT_APP_API_TIMEOUT);

      const duplicateResponse = await axios.post(
        `${API_URL}Appointment/DuplicateAppointment`,
        bookAppointmentPayload,
        { signal: controller.signal }
      );

      if (!duplicateResponse.data) {
        const response = await axios.post(
          `${API_URL}Appointment/CompleteBookAppointment`,
          bookAppointmentPayload,
          { signal: controller.signal }
        );
        clearTimeout(id);
        if (response.data.responseCode === 200) {
          actions.setConfirmed(true);
          if (response.data.bookAppointment.appointmentId) {
            actions.setBookAppointmentId(response.data.bookAppointment.appointmentId);
            setGuid(response.data.bookAppointment.appointmentGUID);
          }
        }
      }
      await logError({
        source: "appointmentSubmitted.completeBookAppointment",
        message: "Success Complete Book Appointment",
        exception: null,
        sessionID: sessionId,
      });
      setLoading(false)
    } catch (error) {
      setLoading(false)
      logActivity(error, `appointmentSubmitted.completeBookAppointment`, "Error in complete book appointment")
      setApiErrorMessage(true);
    }
  };

  const logActivity = async (error, source, message) => {
    const errorMessage = formatErrorMessage(error);
    await logError({
      source: source,
      message: message,
      exception: errorMessage,
      sessionID: sessionId,
    });
  };

  const goBack = () => navigate('/');

  const addAdditionalInfo = (language = null) => {
    actions.clearPhase2Form([]);
    actions.clearState([]);
    navigate(`/patient-checkin/patient-form?appointmentGuid=${guid}`, {
      state: { language, fromFlow: true },
    });
  };

  const renderCarousel = () => (
    <Carousel>
      {["clinic-pic-one", "clinic-pic-two", "clinic-pic-three", "clinic-pic-four"].map(
        (pic, index) => (
          <div key={index}>
            <div className={pic}></div>
          </div>
        ),
      )}
    </Carousel>
  );

  return (
    <>
      <ErrorModal
        visible={apiErrorMessage}
        onClose={() => setApiErrorMessage(false)}
      />
      {location.state ? (
        <div className="appointment-booked-page">
          <Header />
          <Helmet>
            <title>CORA Health | Appointment Submitted</title>
          </Helmet>

          <div className="row no-gutters booked-page-wrapper-row">
            <div className="col-lg-7 content-column">
              <div className="blue-blank-header" />
              <div className="main-content-wrapper">
                <div className="top-info-section">
                  <div className="top-image">
                    <img src={appointmentBooked} alt="Appointment Booked" />
                  </div>
                  <div>
                    <span className="ap-booked-heading">Your appointment is booked.</span>
                    <span className="ap-booked-subheading">A CORA representative will call you if more information is needed.</span>
                  </div>
                </div>

                <div className="ap-details-card p-4">
                  <AppointmentDetails label="Appointment For" value={location.state.therapyName} icon={handIcon} />
                  {location.state.clinicAddress && (
                    <AppointmentDetails label="Clinic Address" value={location.state.clinicAddress.trim()} icon={locationIcon} />
                  )}
                  <div className="date-n-time">
                    <div className="date-icon">
                      <img src={dateIcon} alt="Date Icon" />
                    </div>
                    <div className="date-text">
                      <span className="date-title">Date &amp; Time</span>
                      <span className="date-value">
                        {location.state.date} {' at '} {location.state.time}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="contact-section p-4 pb-0">
                  <div className="cell-number d-flex">
                    <img src={phoneIcon} alt="Phone Number" />
                    <a href={`tel:${location.state.clinicPhone}`} className="ml-3 d-block">
                      {location.state.clinicPhone}
                    </a>
                  </div>
                  <div className="info-text">
                    <span>Please call with questions.</span>
                  </div>
                </div>

                <div className="btns">
                  <SubmitButton disabled={loading} onClick={addAdditionalInfo} text="Begin registration" />
                  <SubmitButton disabled={loading} onClick={() => addAdditionalInfo('spa')} text="Iniciar registro" style={{ marginTop: '15px' }} />
                  <SubmitButton disabled={loading} onClick={() => addAdditionalInfo('creole')} text="Komanse anrejistre" style={{ marginTop: '15px' }} />
                  <Button className="go-home-btn" onClick={goBack} size="large">
                    <img alt="" src={homeIcon} />
                    Go Home
                  </Button>
                </div>
              </div>
            </div>

            <div className="col-lg-5 carousel-column">
              {renderCarousel()}
            </div>
          </div>
        </div>
      ) : (
        <div className="appointment-booked-page">
          <Header />
          <Helmet>
            <title>CORA Health | Appointment Submitted</title>
          </Helmet>

          <div className="row no-gutters wrapper-row">
            <div className="col-lg-7 content-column">
              <div className="blue-blank-header" />
              <div className="main-content-wrapper">
                <div className="top-info-section">
                  <div className="top-image mb-2">
                    <img src={infoSubmitted} alt="Information Submitted" />
                  </div> 
                  <div>
                    <span className="ap-booked-heading">Your information has been submitted.</span>
                    <span className="ap-booked-subheading">A CORA representative will contact you shortly.</span>
                  </div>
                </div>

                <div className="ap-details-card p-4">
                  <div className="contact-section p-4 pb-0">
                    <div className="cell-number d-flex">
                      <img src={phoneIcon} alt="Phone Number" />
                      <a href="tel:8664332672">(866) 443-2672</a>
                    </div>
                    <div className="info-text">
                      <span>Please call with questions.</span>
                    </div>
                  </div>
                </div>

                <div className="btns">
                  <Button className="go-home-btn" onClick={goBack} size="large">
                    <img alt="" src={homeIcon} />
                    Back to Home
                  </Button>
                </div>
              </div>
            </div>

            <div className="col-lg-5 carousel-column">
              {renderCarousel()}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AppointmentSubmitted;
